import * as constants from "../../constant";
import { FlatgroundObject } from "./FlatgroundObject";
import { FlatgroundObjectAdapter } from "./FlatgroundObjectAdapter";
import {
	FoundationExcavationObject,
	type IFoundationExcavationEnvelope,
} from "./FoundationExcavationObject";
import { RoadObject } from "./RoadObject";
import { RoadObjectAdapter } from "./RoadObjectAdapter";
import { TrenchObject } from "./TrenchObject";
import { TrenchObjectAdapter } from "./TrenchObjectAdapter";
import { SolidMeshObject, SolidMeshObjectAdapter } from "./solidMesh";
import type { TSolidMeshVersionedMetaData } from "./solidMesh/SolidMeshObjectAdapter";
import { ObjectType } from "./types";

const VALID_SHAPE_TYPES = ["box", "sphere", "cylinder"] as const;

/**
 * Validate versioned metaData from DB
 */
function parseVersionedMetaData(data: unknown): TSolidMeshVersionedMetaData {
	const parsed = data as {
		version?: number;
		shapeConfig?: { shapeType?: string } & Record<string, unknown>;
		attributes?: Record<string, unknown>;
	};

	if (
		parsed.version === undefined ||
		!parsed.shapeConfig ||
		!parsed.attributes
	) {
		throw new Error(
			"Invalid versioned metaData: missing version, shapeConfig or attributes",
		);
	}

	const shapeType = parsed.shapeConfig.shapeType;
	if (
		!shapeType ||
		!VALID_SHAPE_TYPES.includes(shapeType as (typeof VALID_SHAPE_TYPES)[number])
	) {
		throw new Error(`Invalid shapeType: ${shapeType}`);
	}

	return parsed as TSolidMeshVersionedMetaData;
}

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
export class ObjectFactory {
	static async createObject(data: any) {
		try {
			let type: ObjectType | undefined;
			if ("version" in data.props) {
				// In versioned payloads, props.type should already be ObjectType
				type = data.props.type as ObjectType;
			} else {
				switch (data.props.type) {
					// Legacy payloads: map constants.objectType -> ObjectType
					case constants.objectType.ROAD:
						type = ObjectType.ROAD;
						break;
					case constants.objectType.FLAT:
						type = ObjectType.FLAT_GROUND;
						break;
					case constants.objectType.TRENCH:
						type = ObjectType.TRENCH;
						break;
					default:
						throw new Error("Invalid object type");
				}
			}
			if (!type) {
				throw new Error("Invalid object type");
			}
			switch (type) {
				case ObjectType.ROAD: {
					const roadData = await RoadObjectAdapter.toRoadData(
						data.props,
						data.json,
					);
					if (roadData) {
						const obj = new RoadObject();
						await obj.update(roadData);
						return obj;
					}
					return null;
				}
				case ObjectType.FLAT_GROUND: {
					const flatgroundData = await FlatgroundObjectAdapter.toFlatgroundData(
						data.props,
						data.json,
					);
					if (flatgroundData) {
						const obj = new FlatgroundObject();
						await obj.update(flatgroundData);
						return obj;
					}
					return null;
				}
				case ObjectType.TRENCH: {
					const trenchData = await TrenchObjectAdapter.toTrenchData(
						data.props,
						data.json,
					);
					if (trenchData) {
						const obj = new TrenchObject();
						await obj.update(trenchData);
						return obj;
					}
					return null;
				}
				case ObjectType.FOUNDATION_EXCAVATION: {
					return FoundationExcavationObject.fromEnvelope(
						data.props as IFoundationExcavationEnvelope,
					);
				}
				case ObjectType.SOLID_MESH: {
					// Server returns { props: { version, type, id, name, metaData, ... }, json }
					// id and name are at top-level, not in metaData
					const rawVersionedMetaData = {
						version: data.props.version,
						...data.props.metaData,
					};
					const versionedMetaData =
						parseVersionedMetaData(rawVersionedMetaData);
					const solidMeshData = SolidMeshObjectAdapter.toSolidMeshData(
						versionedMetaData,
						data.props.id,
						data.props.name ?? "",
					);
					if (solidMeshData) {
						const obj = new SolidMeshObject();
						await obj.update(solidMeshData);
						return obj;
					}
					return null;
				}
				default:
					throw new Error(`Invalid object type: ${type}`);
			}
		} catch (error) {
			console.error(error);
			return null;
		}
	}
}
